/* Данные карточки заказа и общий помощник для её запросов: ошибки — тостом, свежий заказ — сразу в кэш. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { money, type Order } from "@okno/shared";
import { api } from "../../lib/api";
import { locKey, useSession } from "../../lib/session";
import type { OrderDetails } from "../../lib/types";
import { useToast } from "../../ui/toast";

/** Карточка заказа: заказ, клиент, авто, смарт-предложение. SSE инвалидирует ключ точки — карточка обновляется сама. */
export function useOrderDetails(orderId: string) {
  const { locationId } = useSession();
  return useQuery({
    queryKey: locKey(locationId, "order", orderId),
    queryFn: () => api.get<OrderDetails>("/locations/" + locationId + "/orders/" + orderId),
  });
}

function isOrder(v: unknown, orderId: string): v is Order {
  return !!v && typeof v === "object" && (v as { id?: unknown }).id === orderId && Array.isArray((v as { items?: unknown }).items);
}

/** Заказ из ответа API: сам заказ или поле order ({ order, refunded }). */
function orderOf(res: unknown, orderId: string): Order | null {
  if (isOrder(res, orderId)) return res;
  if (res && typeof res === "object" && "order" in res) {
    const o = (res as { order: unknown }).order;
    if (isOrder(o, orderId)) return o;
  }
  return null;
}

export interface OrderApi {
  /** /locations/:loc */
  locPath: string;
  /** /locations/:loc/orders/:id */
  path: string;
  /** Выполнить запрос: ошибка → тост и null; заказ из ответа → в кэш карточки; данные точки перезапрашиваются. */
  run: <T>(fn: () => Promise<T>) => Promise<T | null>;
}

export function useOrderApi(orderId: string): OrderApi {
  const { locationId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const locPath = "/locations/" + locationId;
  const path = locPath + "/orders/" + orderId;

  const run = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        const res = await fn();
        const order = orderOf(res, orderId);
        if (order) qc.setQueryData<OrderDetails>(locKey(locationId, "order", orderId), (old) => (old ? { ...old, order } : old));
        void qc.invalidateQueries({ queryKey: locKey(locationId) });
        return res;
      } catch (e) {
        toast.error(e);
        return null;
      }
    },
    [qc, toast, locationId, orderId],
  );

  return useMemo(() => ({ locPath, path, run }), [locPath, path, run]);
}

/** Заказ закрыт: изменения недоступны. */
export const CLOSED: Order["status"][] = ["done", "cancelled", "no_show"];

/** Знак валюты точки (₸, сум…) — для подписей полей «Сумма, ₸». */
export function currencySign(): string {
  return money(0).replace(/^0\s*/, "");
}
